import {
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Box,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  PersonOutline as PersonOutlineIcon,
  Save as SaveIcon,
  Add as AddIcon,
} from "@mui/icons-material";

import React, { useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import WSTextField from "../../Components/TextBox/WSTextBox";
import { FormProvider, useForm } from "react-hook-form";
import WSPhone from "../../Components/Phone/WSPhone";
import WSButton from "../../Components/Button/WSButton";
import {
  callAPI,
  Method,
} from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { useDispatch } from "react-redux";
import { resetDataComponent } from "../../StateManagement/actions";

export const Customer: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id;

  const theme = useTheme();
  const { showMessage } = useSnackbar();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [loadingSaveAndNew, setLoadingSaveAndNew] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

   const location = useLocation();
    const from = location.state?.from;


  const methods = useForm<any>({
    defaultValues: {
      partyType: "Customer",
      partyName: "",
      phoneNumber: "",
      address: "",
      email: "",
    },
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      loadCustomerData(id);
    }
  }, [id]);

  const loadCustomerData = async (customerId: string) => {
    try {
      const response = await callAPI(`Vyapar/Party/${customerId}`, Method.GET);

      let customerData: any = null;
      if (response?.data) customerData = response.data;
      else if (response?.result) customerData = response.result;
      else customerData = response;

      if (customerData && Object.keys(customerData).length > 0) {
        reset({
          partyType: customerData.partyType || "Customer",
          partyName: customerData.partyName || "",
          phoneNumber: customerData.phoneNumber || "",
          address: customerData.address || "",
          email: customerData.email || "",
        });
      } else {
        showMessage("No customer data found", "warning");
      }
    } catch (error: any) {
      showMessage(
        "Failed to fetch customer data: " + (error.message || "Unknown error"),
        "error"
      );
    }
  };

  const prepareRequestData = (data: any) => ({
    partyType: "Customer",
    partyName: data.partyName || "",
    phoneNumber: data.phoneNumber || "",
    address: data.address || "",
    email: data.email || "",
  });

  const handleDataSaveAndNew = useCallback(
    async (data: any) => {
      try {
        setLoadingSaveAndNew(true);
        const request = prepareRequestData(data);
        await callAPI("Vyapar/Party", Method.POST, request);
        reset({
          partyType: "Customer",
          partyName: "",
          phoneNumber: "",
          address: "",
          email: "",
        });
        showMessage("Customer created successfully", "success");
      } catch (error) {
        showMessage("Failed to create customer", "error");
      } finally {
        setLoadingSaveAndNew(false);
      }
    },
    [reset, showMessage]
  );

  const handleData = useCallback(
    async (data: any) => {
      try {
        setLoading(true);
        const request = prepareRequestData(data);

        let response;
        if (isEditMode && id) {
          response = await callAPI(
            `Vyapar/Party/${id}`,
            Method.PUT,
            request
          );
        } else {
          response = await callAPI("Vyapar/Party", Method.POST, request);
        }

        dispatch(resetDataComponent("customerGrid"));
        showMessage(
          response?.message ||
            (isEditMode
              ? "Customer updated successfully"
              : "Customer created successfully"),
          "success"
        );
        
if (response?.success) {
                    if (from === "invoice") {
                        navigate("/invoice");
                    } 

                    else {
                        navigate("/customer/list");
                    }
                }
        // navigate("/customer/list");
      } catch (error) {
        showMessage("Failed to save customer", "error");
      } finally {
        setLoading(false);
      }
    },
    [id, isEditMode, dispatch, showMessage, navigate]
  );

  const handleClose = () => navigate("/customer/list");

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleData)}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              px: 3,
              py: 2,
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      width: 40,
                      height: 40,
                      boxShadow: `0 4px 10px ${alpha(
                        theme.palette.primary.main,
                        0.3
                      )}`,
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        letterSpacing: "-0.5px",
                      }}
                    >
                      {isEditMode ? "Edit Customer" : "Add New Customer"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isEditMode
                        ? "Update customer information"
                        : "Enter customer details to create a new record"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item>
                <Tooltip title="Close">
                  <IconButton
                    onClick={handleClose}
                    sx={{
                      color: theme.palette.text.secondary,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        color: theme.palette.error.main,
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>

          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <WSTextField
                  label="Customer Name"
                  {...methods.register("partyName")}
                  id="partyName"
                  name="partyName"
                  control={methods.control}
                  rules={{ required: "Customer Name is required" }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.background.paper, 0.8),
                      transition: "all 0.2s ease",
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                      "&.Mui-focused": {
                        boxShadow: `0 0 0 3px ${alpha(
                          theme.palette.primary.main,
                          0.1
                        )}`,
                      },
                    },
                  }}
                  suffix={
                    <PersonOutlineIcon
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: 20,
                      }}
                    />
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <WSPhone
                  label="Phone Number"
                  id="phoneNumber"
                  name="phoneNumber"
                  control={methods.control}
                  rules={{ required: "Phone Number is required" }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <WSTextField
                  label="Address"
                  {...methods.register("address")}
                  id="address"
                  name="address"
                  control={methods.control}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.background.paper, 0.8),
                      transition: "all 0.2s ease",
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <WSTextField
                  label="Email"
                  {...methods.register("email")}
                  id="email"
                  name="email"
                  control={methods.control}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.background.paper, 0.8),
                      transition: "all 0.2s ease",
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 3,
            p: 2,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Tooltip title="Save and add another customer">
            <WSButton
              id="saveAndNewBtn"
              variant="outlined"
              color="secondary"
              size="medium"
              label="Save & New"
              onClick={handleSubmit(handleDataSaveAndNew)}
              loading={loadingSaveAndNew}
              startIcon={<AddIcon />}
            />
          </Tooltip>

          <Tooltip title="Save">
            <WSButton
              id="saveBtn"
              variant="contained"
              color="primary"
              size="medium"
              label="Save Customer"
              type="submit"
              startIcon={<SaveIcon />}
              loading={loading}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                boxShadow: `0 4px 12px ${alpha(
                  theme.palette.primary.main,
                  0.3
                )}`,
                "&:hover": {
                  boxShadow: `0 6px 16px ${alpha(
                    theme.palette.primary.main,
                    0.4
                  )}`,
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            />
          </Tooltip>
        </Box>
      </form>
    </FormProvider>
  );
});

export default Customer;
